import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { KeyRound, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const LINK_MESSAGES = {
  apple: "Apple sign-in didn't complete. Please try again.",
  apple_taken: "That Apple account is already linked to another Kindler account",
};

function PasswordDialog({ open, onOpenChange, hasPassword, onSaved }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/auth/password", { current_password: current || null, new_password: next });
      onSaved(data);
      toast.success(hasPassword ? "Password changed" : "Password set");
      setCurrent(""); setNext("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="password-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{hasPassword ? "Change password" : "Set a password"}</DialogTitle><DialogDescription>Use this with your email to sign in.</DialogDescription></DialogHeader>
        {hasPassword && (
          <div className="space-y-1.5"><Label>Current password</Label><Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="password-current-input" /></div>
        )}
        <div className="space-y-1.5"><Label>New password (at least 6 characters)</Label><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="password-new-input" /></div>
        <Button onClick={save} disabled={saving || next.length < 6} className="bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="password-save-button">
          {saving ? "Saving..." : "Save password"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function LinkedSignIns() {
  const [linked, setLinked] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/auth/linked").then(({ data }) => setLinked(data));
    if (params.get("linked") === "apple") toast.success("Apple is now connected");
    const err = params.get("link_error");
    if (err) toast.error(LINK_MESSAGES[err] || LINK_MESSAGES.apple);
    if (params.get("linked") || err) setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!linked) return null;
  const count = ["google", "apple", "password"].filter((m) => linked[m]).length;

  const connectGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/settings";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const connectApple = async () => {
    const { data } = await api.post("/auth/apple/start", { origin: window.location.origin, next: "/settings", link: true });
    window.location.href = data.url;
  };
  const disconnect = async (method) => {
    try {
      const { data } = await api.delete(`/auth/linked/${method}`);
      setLinked(data);
      toast.success("Disconnected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const rows = [
    { key: "google", label: "Google", connect: connectGoogle },
    ...(linked.apple_available || linked.apple ? [{ key: "apple", label: "Apple", connect: connectApple }] : []),
    { key: "password", label: "Email & password", connect: () => setPwOpen(true) },
  ];

  return (
    <div className="bg-white dark:bg-[var(--brand-card-dark)] border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg p-6 mt-8" data-testid="linked-signins">
      <h2 className="font-display text-xl font-semibold flex items-center gap-2"><KeyRound className="h-5 w-5" /> Sign-in methods</h2>
      <p className="text-sm text-muted-foreground mb-4">Choose the ways you can sign in to Kindler. You always keep at least one.</p>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between px-3 py-2.5 rounded-md bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]" data-testid={`signin-row-${r.key}`}>
            <span className="text-sm flex items-center gap-2">
              {r.label}
              {linked[r.key] && <span className="text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1" data-testid={`signin-status-${r.key}`}><CheckCircle2 className="h-3.5 w-3.5" /> Connected</span>}
            </span>
            <div className="flex gap-1">
              {r.key === "password" && linked.password && (
                <Button size="sm" variant="ghost" onClick={() => setPwOpen(true)} data-testid="signin-change-password">Change</Button>
              )}
              {linked[r.key] ? (
                <Button size="sm" variant="ghost" className="text-[var(--brand-crimson)]" disabled={count <= 1} title={count <= 1 ? "Keep at least one way to sign in" : ""} onClick={() => disconnect(r.key)} data-testid={`signin-disconnect-${r.key}`}>Disconnect</Button>
              ) : (
                <Button size="sm" variant="outline" onClick={r.connect} data-testid={`signin-connect-${r.key}`}>{r.key === "password" ? "Set password" : "Connect"}</Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <PasswordDialog open={pwOpen} onOpenChange={setPwOpen} hasPassword={linked.password} onSaved={setLinked} />
    </div>
  );
}
